import { ensureAzureApiAccess } from "components/api/ensureAzureApiAccess";
import { ensureEnterpriseAppApiPermissions } from "components/api/ensureEnterpriseAppApiPermissions";
import { ensureAnyPortalAdmins } from "components/api/roles";
import { getServicePrincipal } from "components/api/servicePrincipal";
import { setSetupDialogActive } from "features/setupDialog";
import { useAuth } from "hooks/hooks";
import { useEffect, useMemo, useState } from "react";
import { successfulConsentFlow } from "utilities/consentUtils/consentFlow";
import { SCOPES } from "utilities/constants/constants";
import { HotjarEventTypes } from "utilities/constants/enums";
import { handleLogout } from "utilities/handleLogout";
import { logHotjarEvent } from "utilities/hotjar/logHotjarEvent";
import { isGlobalAdmin } from "utilities/tokenUtils/getValuesFromToken";
import { ConsentDialog } from "./ConsentDialog";
import { SettingUpPortalDialog } from "./SettingUpPortalDialog";

/*
 * This component is responsible for ensuring that everything is setup correctly.
 * 1. Ensure that the portal is in the tenant
 * 2. Ensure that the portal has the correct permissions
 * 3. Ensure that the portal has at least one portal admin
 *
 * The code is quite convoluted, because:
 * We have to assume that the portal is in the tenant, and that the user has the correct permissions.
 * If we don't, we have to show a dialog to the user, and wait for them to take action.
 *
 * Keeping this in mind, we need to be *very* careful about what we're doing with the responses from the API.
 * We CANNOT assume that a failed API call means that the portal is not in the tenant, or that the user doesn't have the correct permissions.
 * Thus, we need to specifically check for the things we're looking for,
 * and only then assume that the portal is not in the tenant, or that the user doesn't have the correct permissions.
 */
const SetupDialogs = () => {
	const {
		auth: { instance, account, authConfig },
		dispatch,
	} = useAuth();

	const [portalIsInTenant, setPortalIsInTenant] = useState(true);
	const [isMissingPermissions, setIsMissingPermissions] = useState(false);
	const [anyPortalAdmins, setAnyPortalAdmins] = useState(true);
	const [openSettingUpPortalDialog, setOpenSettingUpPortalDialog] = useState(false);
	const [userRoles, setUserRoles] = useState({
		userHasCorrectRoles: true,
		checkingUserRoles: true,
	});

	// Store forceSetup state to persist even if URL changes
	const [isForceSetupMode, setIsForceSetupMode] = useState(false);
	// Track if user has clicked "Enter portal" during force setup
	const [hasUserEnteredPortal, setHasUserEnteredPortal] = useState(false);

	// Check for forceSetup parameter and store it in state
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const forceSetupParam = urlParams.get("forceSetup");
		if (forceSetupParam === "true") {
			setIsForceSetupMode(true);
		}
	}, []);

	useEffect(() => {
		const checkGlobalAdminRole = async () => {
			try {
				const azureToken = await instance.acquireTokenSilent({
					scopes: [SCOPES.GRAPH_READ],
					account,
				});

				const hasAdminRole = isGlobalAdmin(azureToken.accessToken);

				setUserRoles({
					userHasCorrectRoles: hasAdminRole,
					checkingUserRoles: false,
				});
			} catch (error) {
				setUserRoles({
					userHasCorrectRoles: false,
					checkingUserRoles: false,
				});
			}
		};

		if (authConfig && instance) {
			checkGlobalAdminRole();
		}
	}, [authConfig, instance, account]);

	useEffect(() => {
		// Force new Enterprise Application consent for customers that have missing permissions
		const forceReconsentIfWrongAppPermissions = async () => {
			const ensureEnterpriseAppApiPermissionsResponse =
				await ensureEnterpriseAppApiPermissions(instance, account, authConfig);

			setIsMissingPermissions(
				(ensureEnterpriseAppApiPermissionsResponse?.missingPermissions ?? []).length > 0,
			);
		};

		if (authConfig && instance) {
			getServicePrincipal({
				instance,
				account,
				authConfig,
				appId: instance.getConfiguration().auth.clientId,
			})
				.then((servicePrincipal) => {
					if (!servicePrincipal) {
						setPortalIsInTenant(false);
						setAnyPortalAdmins(false);
						return;
					}

					// If we found a service principal, we can assume that the portal is in the tenant
					setPortalIsInTenant(true);
					// We need to check if we have any portal admins
					ensureAnyPortalAdmins(instance, account, authConfig)
						.then((anyAdmins) => {
							setAnyPortalAdmins(anyAdmins);
						})
						.catch(() => {
							// In the case that this call fails,
							// We keep state as is, i.e assume that the portal is in the tenant
						});
				})
				.catch(() => {
					// In the case that this call fails,
					// We keep state as is, i.e assume that the portal is in the tenant
				});

			/* Only run the reconsent flow if we are not coming from a successful one already. */
			if (!successfulConsentFlow(window.location.hash)) {
				forceReconsentIfWrongAppPermissions();
			}

			// Ensure access to tenant
			ensureAzureApiAccess(instance, account, authConfig);
		}
	}, [authConfig, instance, account]);

	// Track setup dialog state for dashboard drawer logic
	const shouldShowConsent = useMemo(() => {
		if (successfulConsentFlow(window.location.hash)) {
			return false;
		}

		if (!portalIsInTenant) {
			return true;
		}

		if (isMissingPermissions) {
			return true;
		}

		return false;
	}, [portalIsInTenant, isMissingPermissions]);

	const shouldShowSetup = useMemo(() => {
		const shouldShow = () => {
			// Development/testing: Force setup dialog via URL parameter
			if (isForceSetupMode) {
				// Force setup mode, BUT respect if user has specifically clicked "Enter portal" in this session
				if (hasUserEnteredPortal) {
					return false;
				}

				return true;
			}

			if (openSettingUpPortalDialog) {
				return true;
			}

			if (!anyPortalAdmins || !portalIsInTenant) {
				setOpenSettingUpPortalDialog(true);
				return true;
			}

			return false;
		};

		const result = shouldShow();

		// Log Hotjar event when setup dialog is shown
		if (result) {
			logHotjarEvent({
				eventType: HotjarEventTypes.SHOW,
				eventText: "setting_up_portal_dialog",
			});
		}

		return result;
	}, [
		openSettingUpPortalDialog,
		anyPortalAdmins,
		portalIsInTenant,
		isForceSetupMode,
		hasUserEnteredPortal,
	]);

	const setupDialogIsActive = shouldShowConsent || shouldShowSetup;

	// Update Redux state when setup dialog state changes
	useEffect(() => {
		dispatch(setSetupDialogActive(setupDialogIsActive));
	}, [setupDialogIsActive, dispatch]);

	const onLogout = () => {
		handleLogout({ account, instance });
	};

	const renderDialog = () => {
		if (shouldShowConsent) {
			return (
				<ConsentDialog
					portalIsInTenant={portalIsInTenant}
					isMissingPermissions={isMissingPermissions}
					userRoles={userRoles}
					redirectUrl={authConfig.ADMIN_CONSENT_URL}
					onLogout={onLogout}
				/>
			);
		}

		if (shouldShowSetup) {
			return (
				<SettingUpPortalDialog
					isMissingPermissions={isMissingPermissions}
					userRoles={userRoles}
					setOpenSettingUpPortalDialog={setOpenSettingUpPortalDialog}
					setPortalIsInTenant={setPortalIsInTenant}
					setAnyPortalAdmins={setAnyPortalAdmins}
					isForceSetup={isForceSetupMode}
					setHasUserEnteredPortal={setHasUserEnteredPortal}
				/>
			);
		}

		return null;
	};

	return <>{renderDialog()}</>;
};

export { SetupDialogs };
